import type { ReactElement, RefObject } from 'react';
import { ChevronRight } from 'lucide-react';
import { useHorizontalOverflow } from '../hooks/useHorizontalOverflow';

export interface OverflowIndicatorProps {
  containerRef: RefObject<HTMLElement | null>;
  recomputeKey: string;
}

/**
 * KIR-4089 right-edge sign that more columns exist off-screen: a soft fade, an
 * arrow, and an "N more columns" count. Clicking scrolls the table right; the
 * sign disappears once the user reaches the far right.
 */
export function OverflowIndicator({
  containerRef,
  recomputeKey,
}: OverflowIndicatorProps): ReactElement | null {
  const { hiddenCount, scrollbarWidth, scrollbarHeight } =
    useHorizontalOverflow(containerRef, recomputeKey);

  if (hiddenCount === 0) return null;

  const label = `${hiddenCount} more ${hiddenCount === 1 ? 'column' : 'columns'}`;
  const scrollRight = (): void => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollBy({
      left: Math.max(container.clientWidth * 0.75, 1),
      // Browsers don't apply prefers-reduced-motion to scrollBy themselves.
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    });
  };

  return (
    <div
      data-testid="table-overflow-indicator"
      // Inset by the scroller's own scrollbars so the fade never covers them.
      style={{ right: scrollbarWidth, bottom: scrollbarHeight }}
      className="pointer-events-none absolute top-0 z-20 flex w-28 items-center justify-end bg-gradient-to-l from-background via-background/70 to-transparent pr-2"
    >
      <button
        type="button"
        onClick={scrollRight}
        aria-label={`Scroll right to see ${label}`}
        className="pointer-events-auto flex flex-col items-center gap-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full border bg-background text-primary shadow-md transition-colors hover:bg-accent">
          <ChevronRight className="h-4 w-4" />
        </span>
        <span className="whitespace-nowrap rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium text-background">
          {label}
        </span>
      </button>
    </div>
  );
}
